/**
 * A CSV the reply linked to, drawn as a table.
 *
 * A skill that builds a schedule answers with a link. As a bare URL that is a
 * dead end: the reader leaves the app, opens a spreadsheet, and comes back to
 * say what they saw. `bimgo-ai-web` renders each CSV link in the reply as a
 * table (`CsvPreview.tsx`), and this is that — the same three states, the same
 * header, the same ten-row preview behind a "show full table" toggle.
 *
 * **The error state is not a formality here.** The desktop's CSP allows fetching
 * from `'self'`, api-v2, the attachments bucket and the local agent, and nothing
 * else — so a CSV hosted anywhere unexpected is blocked before it is read. The
 * reference's error branch already answers that correctly, by naming the failure
 * and leaving a download link, and the link works when the fetch did not because
 * a navigation is not a `connect-src` request.
 *
 * GUID click-to-highlight is deliberately absent; see `lib/csv.ts` for why it is
 * not part of this feature.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { hasHeaderRow, parseCsv } from "../../lib/csv";
import { Spinner } from "../settings/Spinner";

/** The reference's default: ten data rows before the toggle is needed. */
const PREVIEW_ROWS = 10;

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; rows: string[][] };

/** lucide `AlertCircle`. */
function AlertCircleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </svg>
  );
}

/** lucide `Download`. */
function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  );
}

export function CsvPreview({ url }: { url: string }) {
  const { t } = useLocale();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [full, setFull] = useState(false);

  useEffect(() => {
    // A component unmounted mid-fetch — the user switched threads — must not
    // set state afterwards, and must not leave the request running.
    const controller = new AbortController();
    let live = true;
    setState({ kind: "loading" });

    fetch(url, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (!live) return;
        const rows = parseCsv(text);
        if (rows.length === 0) throw new Error(t("csv.empty"));
        setState({ kind: "loaded", rows });
      })
      .catch((error: unknown) => {
        if (!live || controller.signal.aborted) return;
        // A CSP refusal surfaces as a TypeError with no useful message, which
        // would render as "Failed to load CSV: Failed to fetch". The status
        // codes and the parse failures above say more, so they pass through.
        setState({ kind: "error", message: error instanceof Error ? error.message : String(error) });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [url, t]);

  if (state.kind === "loading") {
    return (
      <div className="csv-card is-loading">
        <Spinner size={14} />
        <span>{t("csv.loading")}</span>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="csv-card is-error" role="alert">
        <span className="csv-error-icon" aria-hidden="true">
          <AlertCircleIcon />
        </span>
        <div className="csv-error-body">
          <p className="csv-error-text">{t("csv.loadFailed", { error: state.message })}</p>
          <a className="csv-error-link" href={url} target="_blank" rel="noreferrer noopener">
            {t("csv.downloadFile")}
          </a>
        </div>
      </div>
    );
  }

  // A headerless export — a CAD-layer dump, say — has no column names to show,
  // and taking its first record for a header would both invent titles and lose a
  // row. See `hasHeaderRow`.
  const headed = hasHeaderRow(state.rows);
  const header = headed ? state.rows[0] : undefined;
  const body = headed ? state.rows.slice(1) : state.rows;
  const shown = full ? body : body.slice(0, PREVIEW_ROWS);
  const truncated = body.length > shown.length;
  const columns = header?.length ?? body[0]?.length ?? 0;

  return (
    <div className="csv-card">
      <div className="csv-head">
        <div className="csv-head-left">
          <span className="csv-title">{t("csv.title")}</span>
          <span className="csv-size">
            {t("csv.dimensions", { rows: String(body.length), cols: String(columns) })}
          </span>
        </div>
        <div className="csv-head-right">
          {/* Only offered when there is something folded away — a toggle that
              changes nothing is a control the reader has to test to learn that. */}
          {truncated || full ? (
            <button type="button" className="csv-action" onClick={() => setFull(!full)}>
              {full ? t("csv.showPreview") : t("csv.showFullTable")}
            </button>
          ) : null}
          <a className="csv-action" href={url} target="_blank" rel="noreferrer noopener">
            <DownloadIcon />
            {t("csv.download")}
          </a>
        </div>
      </div>

      {/* The table scrolls inside its own box. A wide schedule must not widen
          the transcript, which would push the whole conversation sideways. */}
      <div className={`csv-scroll${full ? " is-full" : ""}`}>
        <table className="csv-table">
          {header ? (
            <thead>
              <tr>
                <th className="csv-rownum" scope="col">
                  #
                </th>
                {header.map((cell, i) => (
                  <th key={i} scope="col" title={cell}>
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {shown.map((row, r) => (
              <tr key={r}>
                <td className="csv-rownum">{r + 1}</td>
                {row.map((cell, c) => (
                  // `title` because the cell truncates: a GlobalId or a long
                  // family name is unreadable at column width and there is
                  // nowhere else to see it.
                  <td key={c} title={cell}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {truncated ? (
        <div className="csv-foot">
          <span>{t("csv.showingRows", { shown: String(shown.length), total: String(body.length) })}</span>
          <button type="button" className="csv-action" onClick={() => setFull(true)}>
            {t("csv.viewAllRows")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
